import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSList
} from "@wingspanhq/fe-component-library";
import { Stakeholder } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { queriesRoles, queriesStakeholders } from "@query";
import { useSession } from "@hooks/useSession";
import { ErrorAlert } from "@components/ErrorAlert";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { routeHelpersAccountStakeholdersEdit } from "../../paths";
import { useModalRevokeStakeholder } from "../ModalRevokeStakeholder";
import {
  buildStakeholderFiltersConfig,
  STAKEHOLDER_ROLE_FILTER_ALL
} from "./filters/config";
import { StakeholderFilter } from "./filters/types";
import { TableStakeholders } from "./TableStakeholders";

type Props = {
  canManage?: boolean;
};

export const FilterableTableStakeholders: React.FC<Props> = props => {
  const { accountId } = useSession();
  const { urlQuery, setUrlQuery } = useUrlQuery<StakeholderFilter>();

  const goToEdit = routeHelpersAccountStakeholdersEdit.useGoTo();
  const modalRevoke = useModalRevokeStakeholder();

  const roleIdFilter = urlQuery.filter.roleId;
  const selectedRoleId =
    roleIdFilter && roleIdFilter !== STAKEHOLDER_ROLE_FILTER_ALL
      ? roleIdFilter
      : undefined;

  const queryStakeholders =
    queriesStakeholders.listAccountStakeholders.usePaginatedQuery(
      {
        accountId,
        filter: selectedRoleId ? { roleId: { eq: selectedRoleId } } : undefined
      },
      { enabled: !!accountId }
    );
  const queryRoles = queriesRoles.listRoles.useQueryAll();

  const roles = useMemo(() => queryRoles.flatData ?? [], [queryRoles.flatData]);
  const stakeholders = useMemo(
    () => queryStakeholders.flatData ?? [],
    [queryStakeholders.flatData]
  );

  const filtersConfig = useMemo(
    () => buildStakeholderFiltersConfig(roles),
    [roles]
  );

  const totalCount = queryStakeholders.data?.pages?.[0]?.pagination?.totalSize;

  const isLoading = queryStakeholders.isLoading || queryRoles.isLoading;
  const hasFilters = !!selectedRoleId;

  const onEdit = (stakeholder: Stakeholder) => {
    goToEdit({ stakeholderId: stakeholder.id as string });
  };

  const onRevoke = async (stakeholder: Stakeholder) => {
    await modalRevoke.open({
      stakeholderId: stakeholder.id as string,
      accountId
    });
  };

  const renderContent = () => {
    if (isLoading) {
      return <TableStakeholders stakeholders={[]} roles={roles} loading />;
    }

    if (stakeholders.length === 0) {
      return hasFilters ? (
        <WSEmptyState
          type="search"
          title="No results found"
          description="Try adjusting your filters to find what you're looking for."
        />
      ) : (
        <WSEmptyState
          type="people"
          title="No team members yet"
          description="Team members you add will appear here."
        />
      );
    }

    return (
      <WSInfiniteScroll
        onLoad={() => queryStakeholders.fetchNextPage()}
        loadMore={stakeholders.length > 0}
        endOfList={!queryStakeholders.hasNextPage}
        loading={!!queryStakeholders.isFetchingNextPage}
      >
        <TableStakeholders
          stakeholders={stakeholders}
          roles={roles}
          canManage={props.canManage}
          onEdit={onEdit}
          onRevoke={onRevoke}
        />
      </WSInfiniteScroll>
    );
  };

  return (
    <WSList gap="2XL">
      <ErrorAlert
        error={queryStakeholders.error || queryRoles.error}
        context="TeamStakeholder"
      />

      {filtersConfig.length > 0 ? (
        <WSFilters<StakeholderFilter>
          values={urlQuery.filter}
          onFilter={newFilter =>
            setUrlQuery(prev => ({ ...prev, filter: newFilter }))
          }
          primaryFilters={filtersConfig}
          info={totalCount === undefined ? undefined : { count: totalCount }}
        />
      ) : null}

      {renderContent()}
    </WSList>
  );
};
